import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useHoverDropdown } from 'hooks/useHoverDropdown'

export type DropDownMenuRefType = {
  setSelectedItemId: (id: string) => void
}

export type DropDownItem = {
  id: string
  name: string
  value?: any
  isLabel?: boolean
  isShowAllOptions?: boolean
  default?: boolean
}

type Props = {
  items: DropDownItem[]
  onSelected: (item: DropDownItem) => void
  value?: string
  className?: string
  style?: React.CSSProperties
  styleForLabel?: React.CSSProperties
  styleFloatingShadow?: boolean
  styleTableCell?: boolean
  locked?: boolean
  testId?: string
}

const DropDownMenu = forwardRef(
  (
    {
      items,
      onSelected,
      value,
      styleFloatingShadow,
      styleTableCell,
      className,
      style,
      styleForLabel,
      locked,
      testId,
      ...rest
    }: Props,
    ref,
  ) => {
    const {
      isOpen: isShownDropDown,
      isClosingRef,
      open: openDropDown,
      close: closeDropDown,
      closeImmediately: closeDropDownImmediately,
      forceClose,
    } = useHoverDropdown()
    const [selectedItem, setSelectedItem] = useState<DropDownItem>()

    const [htmlWidth, setHtmlWidth] = useState<number>(0)
    const htmlRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLUListElement>(null)

    useEffect(() => {
      if (htmlRef.current) {
        setHtmlWidth((prevWidth) => {
          const currWidth = htmlRef.current?.offsetWidth || 0
          return currWidth > prevWidth ? currWidth : prevWidth
        })
      }
    }, [selectedItem])

    useEffect(() => {
      if (!htmlRef.current || !dropdownRef.current) return

      const updateDropdownWidth = () => {
        if (htmlRef.current && dropdownRef.current) {
          dropdownRef.current.style.width = `${htmlRef.current.offsetWidth}px`
          dropdownRef.current.style.minWidth = `${htmlRef.current.offsetWidth}px`

          // For styleTableCell with position: fixed, always calculate position (even when hidden)
          if (styleTableCell) {
            const rect = htmlRef.current.getBoundingClientRect()
            dropdownRef.current.style.top = `${rect.bottom}px`
            dropdownRef.current.style.left = `${rect.left}px`
          }
        }
      }

      // Observe label size changes
      const observer = new window.ResizeObserver(updateDropdownWidth)
      observer.observe(htmlRef.current)
      // Also update on window resize
      window.addEventListener('resize', updateDropdownWidth)
      // Also update when dropdown is shown
      updateDropdownWidth()

      return () => {
        observer.disconnect()
        window.removeEventListener('resize', updateDropdownWidth)
      }
    }, [isShownDropDown, styleTableCell])

    const getItemName = () => {
      if (selectedItem) {
        // check if selected item exists in the items list, otherwise return the first item
        const tmpSelectedItem = items.find(
          (item) => item.id === selectedItem.id,
        )
        if (!tmpSelectedItem) {
          setSelectedItem(items[0])
          return items[0].name
        }

        // Check if the selected item is marked with defaultValue: true
        if (selectedItem.isShowAllOptions) {
          // Find the item marked as a label and return its name
          const labelItem = items.find((item) => item.isLabel)
          return labelItem ? labelItem.name : ''
        }
        // For other items, return their live name from items list
        return tmpSelectedItem.name
      }

      return items.length > 0 ? items[0].name : ''
    }

    const onSelectItem = (id: string) => {
      const item = items.find((item) => id === item.id)
      if (item) {
        setSelectedItem(item)
        onSelected(item)
      }
      forceClose()
    }

    const onClickDropDown = (e: React.MouseEvent) => {
      // Don't process if clicking on a menu item - let the item handle it
      if ((e.target as HTMLElement).tagName === 'LI') {
        return
      }
      // Skip if the dropdown is locked
      if (locked || isClosingRef.current) {
        return
      }
    }

    const hasItem = (): boolean => {
      // Check if there are any selectable items (non-label items excluding the currently selected one)
      const selectableItems = items.filter(
        (item) => !item.isLabel && item.id !== selectedItem?.id,
      )
      return selectableItems.length > 0
    }

    const onMouseEnterDropDown = () => {
      // Skip if there are no items to show
      if (!hasItem()) {
        return
      }
      // Skip if the dropdown is locked
      if (locked) {
        closeDropDownImmediately()
        return
      }
      openDropDown()
    }

    const onMouseLeaveDropDown = closeDropDown

    // Initialize selected item based on value, default, or first non-label item
    useEffect(() => {
      if (value) {
        const selectedItemBasedOnValue = items.find(
          (item) => item.value === value,
        )
        if (selectedItemBasedOnValue) {
          setSelectedItem(selectedItemBasedOnValue)
          return
        }
      }

      // find default item
      const defaultItem = items.find((item) => item.default)
      if (defaultItem) {
        setSelectedItem(defaultItem)
        return
      }

      if (items.length > 0 && !items[0].isLabel) {
        setSelectedItem(items[0])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Update selected item if its name changes in the items list
    useEffect(() => {
      if (selectedItem) {
        const item = items.find((item) => selectedItem.id === item.id)
        if (item && selectedItem.name !== item.name) {
          setSelectedItem(item)
        }
      }
    }, [items, selectedItem])

    useImperativeHandle(ref, () => ({
      setSelectedItemId: (id: string) => {
        const item = items.find((item) => id === item.id)
        if (item) {
          setSelectedItem(item)
        } else if (items.length > 0) {
          setSelectedItem(items[0])
        }
      },
    }))

    const bordered = !styleTableCell && !styleFloatingShadow
    const hasSelectableItems = hasItem()

    const internals = (
      <>
        <div
          ref={htmlRef}
          className={
            `dropdown-box flex min-h-[36px] w-full
            min-w-[188px] items-center justify-center px-3 ` +
            (!styleTableCell && !styleFloatingShadow
              ? 'border border-primaryGrey'
              : '')
          }
          style={{
            ...(styleFloatingShadow && {
              boxShadow: `
                0px 206px 58px 0px rgba(0, 0, 0, 0.00), 
                0px 132px 53px 0px rgba(0, 0, 0, 0.01), 
                0px 74px 45px 0px rgba(0, 0, 0, 0.05), 
                0px 33px 33px 0px rgba(0, 0, 0, 0.09), 
                0px 8px 18px 0px rgba(0, 0, 0, 0.10)
              `,
            }),
            ...(styleTableCell && { height: 52 }),
            paddingRight: 7,
            cursor: 'pointer',
            backgroundColor: 'white',
          }}
        >
          <div className="grow" />
          <label
            tabIndex={0}
            className="mentem-dropdown-button mr-4 max-w-[400px] whitespace-normal text-center"
            style={styleForLabel}
          >
            {getItemName()}
          </label>
          <div className="grow" />
          {!locked && hasSelectableItems && (
            <svg
              width="14"
              height="9"
              viewBox="0 0 14 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                minWidth: '14px',
                marginTop: '2px',
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.292893 0.511643C0.653377 0.151159 1.22061 0.12343 1.6129 0.428455L1.70711 0.511643L7 5.80375L12.2929 0.511643C12.6534 0.151159 13.2206 0.12343 13.6129 0.428455L13.7071 0.511643C14.0676 0.872127 14.0953 1.43936 13.7903 1.83165L13.7071 1.92586L7.70711 7.92586C7.34662 8.28634 6.77939 8.31407 6.3871 8.00905L6.29289 7.92586L0.292893 1.92586C-0.0976311 1.53533 -0.0976311 0.902168 0.292893 0.511643Z"
                fill="#51597B"
              />
            </svg>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            justifyItems: 'center',
          }}
        >
          <ul
            ref={dropdownRef}
            tabIndex={0}
            className="mentem-scrollbar-2 dropdown-content bg-base-100 max-h-[60vh] overflow-y-auto shadow"
            style={{
              ...(htmlWidth && {
                width: `${htmlWidth}px`,
                minWidth: `${htmlWidth}px`,
              }),
              ...(isShownDropDown
                ? {
                    opacity: 1,
                    visibility: 'visible',
                  }
                : {
                    opacity: 0,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                  }),
            }}
            {...(testId && { 'data-testid': `${testId}-menu` })}
          >
            {items
              .filter((item) => !item.isLabel && item.id !== selectedItem?.id)
              .map((item) => (
                <li
                  key={item.id + item.name + item.value}
                  className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-center hover:bg-[#FFDC0026]"
                  onClick={() => onSelectItem(item.id)}
                >
                  {item.name}
                </li>
              ))}
          </ul>
        </div>
      </>
    )

    if (!styleTableCell) {
      return (
        <div
          tabIndex={-1}
          className={`${hasSelectableItems ? 'dropdown' : ''} ${
            isShownDropDown ? 'dropdown-open' : ''
          } ${className || ''} ${bordered ? 'bordered' : ''}`}
          onClick={onClickDropDown}
          onMouseEnter={hasSelectableItems ? onMouseEnterDropDown : undefined}
          onMouseLeave={hasSelectableItems ? onMouseLeaveDropDown : undefined}
          style={{
            display: 'grid',
            justifyItems: 'center',
            position: 'relative',
            ...(style || {}),
          }}
          {...(testId && { 'data-testid': testId })}
          {...rest}
        >
          {internals}
        </div>
      )
    } else {
      return (
        <div
          tabIndex={-1}
          style={{
            height: '100%',
            width: '100%',
            display: 'inline-block',
          }}
          className={`${hasSelectableItems ? 'dropdown' : ''} ${
            isShownDropDown ? 'dropdown-open' : ''
          } styleTableCell ${className || ''}`}
          onClick={onClickDropDown}
          onMouseEnter={hasSelectableItems ? onMouseEnterDropDown : undefined}
          onMouseLeave={hasSelectableItems ? onMouseLeaveDropDown : undefined}
          {...(testId && { 'data-testid': testId })}
          {...rest}
        >
          <div
            className="crazyHACK"
            style={{
              height: '100%',
              width: '100%',
              display: 'grid',
              justifyItems: 'center',
              position: 'relative',
              verticalAlign: 'middle',
              ...(style || {}),
            }}
          >
            {internals}
          </div>
        </div>
      )
    }
  },
)

export default DropDownMenu
